import { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import ValidateAccess from '../access/ValidateAccess';
import NoAccess from '../access/NoAccess';
import Offline from '../access/Offline';
import { isBoolean } from '../helpers/ObjectHelpers';
import { isLoggedIn, onGuestCheckAsync } from '../helpers/AuthHelpers';
import { RouteGuardStates } from '../../services/SystemNames';
import { MinWidthRequired } from '../access/MinWidthRequired';
import { useMinWidthRequired } from '../hooks/useMinWidthRequired';
import { ApplicationRightService } from 'services/ApplicationRightService';
import Login from '../access/Login';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { ApplicationRightSystemNames } from '../constants/ApplicationRightSystemNames';
import { useApplicationRights } from "components/hooks/useAppStore";

export const RouteGuardApplicationRights = ({
    children,
    requiredApplicationRights = [],
    beta = false,
    minWidth,
    doGuestCheck = false,
    onlineOnly = true,
}) => {

    const location = useLocation();
    const [guardState, setGuardState] = useState(RouteGuardStates.Busy);
    const hasMinWidth = useMinWidthRequired(minWidth, true);

    const isOnline = useNetworkStatus();

    const applicationRights = useApplicationRights();

    const canAccess = () => {
        if (!applicationRights) return false;

        const applicationRightService = new ApplicationRightService(applicationRights);
        let validBetaCheck = true;
        if (isBoolean(beta)) {
            if (beta) {
                validBetaCheck = applicationRightService.canAccess([ApplicationRightSystemNames.ACCESS_TO_BETA_PROGRAM]);
            }
        }
        
        return applicationRightService.canAccess(requiredApplicationRights) && validBetaCheck;
    }

    const initializeOffline = () => {
        if (onlineOnly) {
            setGuardState(RouteGuardStates.Offline);
        } else {
            if (!canAccess()) {
                setGuardState(RouteGuardStates.NoAccess);
            } else {
                setGuardState(RouteGuardStates.Valid);
            }
        }
    }

    const initializeOnline = () => {
        if (!canAccess()) {
            setGuardState(RouteGuardStates.NoAccess);
        } else {
            if (doGuestCheck) {
                onGuestCheckAsync();
            }
            setGuardState(RouteGuardStates.Valid);
        }
    }

    const initialize = () => {
        const applicationRightService = new ApplicationRightService(applicationRights);
        const canAccessPilotWeb = applicationRightService.canAccess([ApplicationRightSystemNames.NJORD_PILOT_WEB_LOGIN]);

        if (!isLoggedIn() || !canAccessPilotWeb) {
            setGuardState(RouteGuardStates.Login);
        } else {
            if (isOnline) {
                initializeOnline();
            } else {
                initializeOffline();
            }
        }
    }

    useEffect(() => {
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, isOnline, applicationRights])
    
    return (
        hasMinWidth ?
            (() => {
                switch (guardState)
                {
                    case RouteGuardStates.Valid:
                        return (children);
                    case RouteGuardStates.Offline:
                        return (<Offline />);
                    case RouteGuardStates.NoAccess:
                        return (<NoAccess />);
                    case RouteGuardStates.Login:
                        return (<Login />);

                    default:
                        return (<ValidateAccess />);
                }
            })()
            :
            <MinWidthRequired minWidth={minWidth} />
    )
};
